import { useQuery } from "@tanstack/react-query";
import { handleError } from "@/api/error";
import { client, useScopedMerchantClient } from "./scoped-merchant-client";

export const useAssuranceCreator = (creatorId: string) => {
  const scope = useScopedMerchantClient();

  return useQuery({
    queryKey: ["creator-metrics-by-id", scope.merchantId, scope.workspaceId, creatorId],
    queryFn: async () => {
      if (!creatorId) return null;

      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].assurance.creator[":creatorId"].$get({
            param: { ...scope.param, creatorId },
          })
        : await client.merchant[":merchantId"].assurance.creator[":creatorId"].$get({
            param: { ...scope.param, creatorId },
          });

      if (!res.ok) {
        if (res.status === 404) {
          return null;
        }
        throw await handleError(res);
      }

      const creatorData = await res.json();

      return {
        ...creatorData,
        videos: creatorData.videoCount,
        videoList:
          creatorData.videos?.map((video) => ({
            videoId: video.videoId,
            videoInfo: video.videoTitle,
            publishTime: video.publishDate,
            thumbnail: video.thumbnail,
            views: video.views,
            revenue: video.gmv,
            gpm: video.gpm,
          })) ?? [],
        revenue: creatorData.gmv,
      };
    },
    enabled: !!scope.merchantId,
  });
};

export const useCreator = (id?: string) => {
  const scope = useScopedMerchantClient();

  return useQuery({
    queryKey: ["creator", scope.merchantId, scope.workspaceId, id],
    queryFn: async () => {
      if (!id) throw new Error("Missing creatorId");
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].creator[":creatorId"].$get({
            param: { ...scope.param, creatorId: id },
          })
        : await client.merchant[":merchantId"].creator[":creatorId"].$get({
            param: { ...scope.param, creatorId: id },
          });

      if (!res.ok) {
        if (res.status === 404) {
          return null;
        }
        throw await handleError(res);
      }

      const creatorData = await res.json();

      return creatorData;
    },
    enabled: !!scope.merchantId && !!id,
  });
};

export const useCreators = (search?: string) => {
  const scope = useScopedMerchantClient();
  return useQuery({
    queryKey: ["creators", scope.merchantId, scope.workspaceId, search],
    queryFn: async () => {
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].creator.$get({
            param: scope.param,
            query: { search },
          })
        : await client.merchant[":merchantId"].creator.$get({
            param: scope.param,
            query: { search },
          });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    enabled: !!scope.merchantId,
  });
};

const CREATORS_BATCH_CHUNK_SIZE = 200;

export const useCreatorsByIds = (creatorIds: string[]) => {
  const scope = useScopedMerchantClient();
  const uniqueIds = Array.from(new Set(creatorIds)).sort();

  return useQuery({
    queryKey: ["creators-batch", scope.merchantId, scope.workspaceId, uniqueIds],
    queryFn: async () => {
      const chunks: string[][] = [];
      for (let i = 0; i < uniqueIds.length; i += CREATORS_BATCH_CHUNK_SIZE) {
        chunks.push(uniqueIds.slice(i, i + CREATORS_BATCH_CHUNK_SIZE));
      }

      const results = await Promise.all(
        chunks.map(async (chunk) => {
          const res = scope.workspaceId
            ? await client.workspace[":workspaceId"].merchant[":merchantId"].creator.batch.$post({
                param: scope.param,
                json: { creatorIds: chunk },
              })
            : await client.merchant[":merchantId"].creator.batch.$post({
                param: scope.param,
                json: { creatorIds: chunk },
              });
          if (!res.ok) throw await handleError(res);
          return res.json();
        }),
      );

      return Object.assign({}, ...results) as (typeof results)[0];
    },
    enabled: !!scope.merchantId && uniqueIds.length > 0,
    staleTime: 5 * 60 * 1000,
  });
};
